import { useState, useRef, useEffect } from 'react';
import { Pencil, Trash2, SmilePlus, FileText, Download, MessageSquare, Pin, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { getColorClasses, type ProfileColorKey } from '@maios/shared';
import type { ChatMessage, ResolvedMention } from '../../services/chatService';
import { editMessage, deleteMessage, toggleReaction, pinMessage, unpinMessage } from '../../services/chatChannelService';
import EmojiPicker from './EmojiPicker';
import ReactionBadges from './ReactionBadges';
import ImageLightbox from './ImageLightbox';
import MentionCard from './MentionCard';
import MessageToTaskFlow from './MessageToTaskFlow';
import MessageLinkedResourcesBadge from './MessageLinkedResourcesBadge';
import Avatar from '../common/Avatar';
import UserDisplayName from '../common/UserDisplayName';
import { useProfileModal } from '../common/ProfileModalProvider';
import { linkifyText } from '../../utils/linkifyText';

function formatTime(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h < 12 ? '오전' : '오후'} ${h % 12 || 12}:${m}`;
}

export function DateSeparator({ date }: { date: string }) {
  const d = new Date(date);
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  const label = `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-sm text-gray-400 whitespace-nowrap">{label}</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

export function isSameDay(a: string, b: string) {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

// CHG-131 REQ-CP-017: 사용자 멘션 — displayColor/nickname 동행
interface UserMention {
  userId: string;
  name: string;
  offset: number;
  length: number;
  displayColor?: ProfileColorKey | null;
  nickname?: string | null;
}

/** Render a plain text segment with issue mention highlighting */
function renderIssueParts(
  text: string, mentionKeys: Set<string>, resolvedMap: Map<string, ResolvedMention>,
  compact: boolean, keyPrefix: string,
  onMentionClick?: (issueKey: string) => void, onMentionCardClick?: (issueId: string) => void,
) {
  const parts = text.split(/(#[A-Z]+-\d+)/g);
  return parts.map((part, i) => {
    const match = part.match(/^#([A-Z]+-\d+)$/);
    if (match && mentionKeys.has(match[1])) {
      const resolved = resolvedMap.get(match[1]);
      if (resolved) {
        return <MentionCard key={`${keyPrefix}-${i}`} issueKey={resolved.issueKey} title={resolved.title}
          statusName={resolved.statusName} statusColor={resolved.statusColor} issueType={resolved.issueType}
          assignee={resolved.assignee} compact={compact} onClick={() => onMentionCardClick?.(resolved.issueId)} />;
      }
      // CHG-131 REQ-CP-016: 이슈 멘션 색을 본인/상대 무관 단일 색(blue-600)으로 통일
      return <span key={`${keyPrefix}-${i}`} className="text-blue-600 hover:underline cursor-pointer font-medium"
        onClick={(e) => { e.stopPropagation(); onMentionClick?.(match[1]); }}>{part}</span>;
    }
    // CHG-150 Phase B-1: 이슈 키가 아닌 plain text 영역의 URL을 자동 링크로 변환
    return <span key={`${keyPrefix}-${i}`}>{linkifyText(part, `${keyPrefix}-${i}-l`)}</span>;
  });
}

function renderMessageContent(
  content: string,
  mentions: { issueKey?: string; issueId?: string; userId?: string; name?: string; offset?: number; length?: number; displayColor?: ProfileColorKey | null; nickname?: string | null }[],
  resolvedMentions: ResolvedMention[] | undefined,
  compact: boolean,
  onMentionClick?: (issueKey: string) => void,
  onMentionCardClick?: (issueId: string) => void,
  onUserMentionClick?: (userId: string) => void,
) {
  // Separate issue mentions and user mentions
  const issueMentions = mentions.filter((m) => m.issueKey) as { issueKey: string; issueId: string }[];
  const userMentions = (mentions.filter((m) => m.userId && typeof m.offset === 'number') as UserMention[])
    .sort((a, b) => a.offset - b.offset);

  const mentionKeys = new Set(issueMentions.map((m) => m.issueKey));
  const resolvedMap = new Map((resolvedMentions ?? []).map((m) => [m.issueKey, m]));

  // If no user mentions, fall back to issue-only rendering
  if (userMentions.length === 0) {
    return renderIssueParts(content, mentionKeys, resolvedMap, compact, 'r', onMentionClick, onMentionCardClick);
  }

  // Split content by user mention offsets
  const result: React.ReactNode[] = [];
  let lastIdx = 0;

  userMentions.forEach((um, i) => {
    // Text before this mention
    if (um.offset > lastIdx) {
      const seg = content.substring(lastIdx, um.offset);
      result.push(...renderIssueParts(seg, mentionKeys, resolvedMap, compact, `s${i}`, onMentionClick, onMentionCardClick));
    }
    // The mention itself
    const mentionText = content.substring(um.offset, um.offset + um.length);
    const isAll = um.userId === '@all';
    const isHere = um.userId === '@here';
    let cls = 'rounded px-0.5 font-medium ';
    let clickable = false;
    // CHG-131 REQ-CP-017: @all/@here는 의미색 유지, @사용자는 displayColor의 soft 조합
    if (isAll) {
      cls += 'bg-yellow-100 text-yellow-700';
    } else if (isHere) {
      cls += 'bg-green-100 text-green-700';
    } else {
      const palette = getColorClasses(um.displayColor ?? undefined);
      cls += `${palette.softBgClass} ${palette.softTextClass} cursor-pointer hover:underline`;
      clickable = true;
    }
    result.push(
      <span
        key={`um${i}`}
        className={cls}
        onClick={clickable ? (e) => { e.stopPropagation(); onUserMentionClick?.(um.userId); } : undefined}
      >
        {mentionText}
      </span>,
    );
    lastIdx = um.offset + um.length;
  });

  // Remaining text
  if (lastIdx < content.length) {
    const seg = content.substring(lastIdx);
    result.push(...renderIssueParts(seg, mentionKeys, resolvedMap, compact, 'e', onMentionClick, onMentionCardClick));
  }

  return result;
}

const fmtSize = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

function formatRelativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}시간 전`;
  const days = Math.floor(hrs / 24);
  return `${days}일 전`;
}

// ─── 메시지 (모두 좌측 정렬) ───
export default function ChatMessageItem({
  message, isMine, showSender, onMentionClick, onMentionCardClick, compact,
  isHighlighted, channelId, currentUserId,
  onOpenThread, isThreadReply, isThreadRoot,
  currentUserRole, onPinChange,
  channelType, contextProjectId, contextProjectKey,
}: {
  message: ChatMessage;
  isMine: boolean;
  showSender: boolean;
  onMentionClick?: (issueKey: string) => void;
  onMentionCardClick?: (issueId: string) => void;
  compact?: boolean;
  isHighlighted?: boolean;
  channelId?: string;
  currentUserId?: string;
  onOpenThread?: (messageId: string) => void;
  isThreadReply?: boolean;
  isThreadRoot?: boolean;
  currentUserRole?: string | null;
  onPinChange?: () => void;
  // CHG-123 REQ-V123-021: 메시지 → 작업/할 일 변환 컨텍스트
  channelType?: 'PROJECT' | 'USER' | 'DM';
  contextProjectId?: string;
  contextProjectKey?: string;
}) {
  const sender = message.sender;
  // CHG-131 REQ-CP-014/016: 버블/색 분기 변수(bubbleColor/bubbleRound/bubblePadding) 모두 제거.
  // 본문 색은 본인/상대 무관 text-gray-900 단일.
  const avatarSize: 'xs' | 'sm' = compact ? 'xs' : 'sm';
  const textSize = compact ? 'text-sm' : 'text-sm';
  const highlightClass = isHighlighted ? 'bg-yellow-50' : '';

  // CHG-131: 본인/타인 구분 없이 ProfileModal 표시 (편집 흐름은 GNB → 프로필 메뉴에서)
  const { openProfileModal } = useProfileModal();
  const handleProfileClick = (userId: string) => openProfileModal(userId);

  const isDeleted = (message as unknown as Record<string, unknown>).isDeleted === true;
  const isEdited = (message as unknown as Record<string, unknown>).isEdited === true;
  const isPinned = (message as unknown as Record<string, unknown>).isPinned === true;
  const isOwn = currentUserId ? message.sender.userId === currentUserId : isMine;
  const canEdit = isOwn && !isDeleted && !!channelId;
  const canPin = !isThreadReply && !isDeleted && !!channelId && (currentUserRole === 'OWNER' || currentUserRole === 'ADMIN');

  const reactions = (message as unknown as Record<string, unknown>).reactions as
    Array<{ emoji: string; count: number; users: string[]; reacted: boolean }> | undefined;
  const attachments = ((message as unknown as Record<string, unknown>).attachments ?? []) as
    Array<{ id: string; fileName: string; fileSize: number; mimeType: string; url: string; thumbnailUrl?: string | null }>;
  const imageAttachments = attachments.filter((a) => a.mimeType.startsWith('image/'));

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerDirection, setPickerDirection] = useState<'up' | 'down'>('up');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // CHG-123 REQ-V123-021: 작업/할 일 변환 모달 오픈 상태
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const isSystemMessage = (message as unknown as Record<string, unknown>).isSystemMessage === true;
  const canOpenTaskModal = !isDeleted && !isSystemMessage;

  // CHG-123: 모달 열림 중 메시지가 삭제되면 닫고 경고 (자기 검증 #3)
  useEffect(() => {
    if (taskModalOpen && isDeleted) {
      console.warn('[CHG-123] 변환 모달 열린 상태에서 메시지가 삭제됨 — 모달 닫기');
      setTaskModalOpen(false);
    }
  }, [taskModalOpen, isDeleted]);
  const menuRef = useRef<HTMLDivElement>(null);
  const pickerBtnRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!showPicker) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showPicker]);

  const handleEdit = async () => {
    if (!channelId || !editContent.trim()) return;
    setSaving(true);
    try {
      await editMessage(channelId, message.messageId, editContent.trim());
      setIsEditing(false);
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '수정에 실패했습니다.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!channelId) return;
    try {
      await deleteMessage(channelId, message.messageId);
      setConfirmDelete(false);
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '삭제에 실패했습니다.');
    }
  };

  const handleReactionToggle = async (emoji: string) => {
    if (!channelId) return;
    try { await toggleReaction(channelId, message.messageId, emoji); } catch { /* socket will update */ }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setIsEditing(false); setEditContent(message.content); }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !e.nativeEvent.isComposing) handleEdit();
  };

  const handlePin = async () => {
    if (!channelId) return;
    try {
      await pinMessage(channelId, message.messageId);
      toast.success('메시지가 고정되었습니다.');
      onPinChange?.();
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '고정에 실패했습니다.');
    }
  };

  const handleUnpin = async () => {
    if (!channelId) return;
    try {
      await unpinMessage(channelId, message.messageId);
      toast.success('메시지 고정이 해제되었습니다.');
      onPinChange?.();
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '고정 해제에 실패했습니다.');
    }
  };

  // CHG-131 REQ-CP-015: 슬랙형 레이아웃 — showSender 시 group으로 묶어 hover 시 시간 표시
  // 메시지 row 전체에 hover 하이라이트(action 버튼이 화면 우측 정렬이라 어떤 메시지 위인지 명확히 식별 가능하도록).
  return (
    <>
      <div className={`flex gap-2 ${showSender ? 'mt-3' : 'mt-0.5'} ${highlightClass} rounded-lg group relative hover:bg-gray-100 -mx-2 px-2 py-0.5`}>
        {/* Avatar */}
        {showSender ? (
          <Avatar
            userId={sender.userId}
            name={sender.name}
            firstName={sender.firstName}
            avatarUrl={sender.profileImage}
            displayColor={sender.displayColor ?? undefined}
            size={avatarSize}
            onClick={() => handleProfileClick(sender.userId)}
          />
        ) : (
          // showSender=false: 아바타 폭만 차지, height는 row에 자연 stretch.
          // hover 시간 span에 leading-5(text-sm와 동일 line-height) → 메시지 첫 줄 baseline과 정렬.
          // 여러 줄 메시지에서도 시간이 첫 줄과 같은 y-위치에 표시된다.
          <div className={`${compact ? 'w-6' : 'w-8'} shrink-0 flex items-start justify-end pr-1`}>
            <span className={`text-[11px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap ${compact ? 'leading-4' : 'leading-5'}`}>
              {formatTime(message.createdAt).replace(/오[전후]\s/, '')}
            </span>
          </div>
        )}

        {/* Content area */}
        <div className="min-w-0 flex-1">
          {showSender && (
            <div className="flex items-center gap-2 mb-1">
              <UserDisplayName
                userId={sender.userId}
                name={sender.name}
                nickname={sender.nickname}
                displayColor={sender.displayColor ?? undefined}
                bold
                onClick={() => handleProfileClick(sender.userId)}
                className={textSize}
              />
              <span className="text-sm text-gray-400">{formatTime(message.createdAt)}</span>
              {isEdited && !isDeleted && <span className="text-[11px] text-gray-400">(수정됨)</span>}
            </div>
          )}

          {/* Message body — 액션 버튼은 row 우상단에 floating overlay로 분리됨 (좁은 사이드 패널에서도 메시지 폭 확보) */}
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <div className="max-w-md">
                  <textarea ref={textareaRef} value={editContent} onChange={(e) => setEditContent(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={Math.min(editContent.split('\n').length + 1, 5)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-300" />
                  <div className="flex justify-end gap-2 mt-1">
                    <button onClick={() => { setIsEditing(false); setEditContent(message.content); }}
                      className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1">취소</button>
                    <button onClick={handleEdit} disabled={saving || !editContent.trim()}
                      className="text-sm bg-gray-900 text-white px-3 py-1 rounded disabled:opacity-50">
                      {saving ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              ) : isDeleted ? (
                <div className={`${textSize}`}>
                  <span className="italic text-gray-400">이 메시지는 삭제되었습니다.</span>
                </div>
              ) : (
                <>
                  {message.content && (
                    // CHG-131 REQ-CP-014: 버블/패딩/너비 제한 모두 제거. break-words만으로 가로 스크롤 차단.
                    <div className={`break-words ${textSize} whitespace-pre-wrap text-gray-900`}>
                      {renderMessageContent(message.content, message.mentions, message.resolvedMentions, !!compact, onMentionClick, onMentionCardClick, handleProfileClick)}
                    </div>
                  )}
                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="flex flex-col gap-1.5 mt-1 max-w-sm">
                      {attachments.map((att) => att.mimeType.startsWith('image/') ? (
                        <img key={att.id} src={att.thumbnailUrl || att.url} alt={att.fileName}
                          className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 border bg-gray-100"
                          style={{ maxHeight: 320 }}
                          loading="lazy"
                          onClick={() => setLightboxIndex(imageAttachments.findIndex((a) => a.id === att.id))} />
                      ) : (
                        <div key={att.id} className="flex items-center gap-3 bg-gray-50 border rounded-lg px-4 py-3 max-w-xs">
                          <FileText className="size-5 text-gray-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{att.fileName}</p>
                            <p className="text-sm text-gray-500">{fmtSize(att.fileSize)}</p>
                          </div>
                          <a href={att.url} download={att.fileName} className="text-blue-500 hover:text-blue-700 shrink-0">
                            <Download className="size-4" />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {/* CHG-123 REQ-V123-050: linkedResources 배지 (작업/할 일) */}
              {!isDeleted && message.linkedResources && (
                <MessageLinkedResourcesBadge
                  linkedResources={message.linkedResources}
                  isMine={isMine}
                />
              )}
              {/* Reactions */}
              {!isDeleted && reactions && reactions.length > 0 && (
                <ReactionBadges reactions={reactions} onToggle={handleReactionToggle}
                  onAddReaction={() => setShowPicker(true)} />
              )}
              {/* Thread entry button */}
              {!isThreadReply && !isThreadRoot && !isDeleted && (message.threadCount ?? 0) > 0 && onOpenThread && (
                <button
                  onClick={() => onOpenThread(message.messageId)}
                  className="flex items-center gap-2 px-3 py-1 mt-1 rounded-md hover:bg-gray-100 text-sm text-blue-600 cursor-pointer"
                >
                  <div className="flex -space-x-1">
                    {/* CHG-131: 스레드 프리뷰 아바타도 공용 Avatar로 통일. firstName 우선, 없으면 name 첫 글자. */}
                    {(message.threadPreview ?? []).map((user) => (
                      <div key={user.userId} className="border-2 border-white rounded-full">
                        <Avatar
                          userId={user.userId}
                          name={user.name}
                          firstName={user.firstName}
                          avatarUrl={user.profileImage}
                          displayColor={user.displayColor ?? undefined}
                          size="xs"
                        />
                      </div>
                    ))}
                  </div>
                  <span>{message.threadCount}개의 답글</span>
                  {message.lastThreadAt && (
                    <span className="text-gray-400 text-sm">마지막 답글 {formatRelativeTime(message.lastThreadAt)}</span>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>

        {/* CHG-131: 슬랙형 floating 액션 바 — row 우상단에 absolute로 오버랩.
            inline 자리를 차지하지 않아 좁은 사이드 패널에서도 메시지가 본문 폭 전체를 사용. */}
        {!isEditing && !isDeleted && (channelId || canEdit) && !isThreadRoot && (
          <div
            ref={menuRef}
            className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-3 -top-3 z-10 flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-sm px-0.5 py-0.5"
          >
            {channelId && (
              <button ref={pickerBtnRef} onClick={() => {
                if (!showPicker && pickerBtnRef.current) {
                  const rect = pickerBtnRef.current.getBoundingClientRect();
                  const spaceBelow = window.innerHeight - rect.bottom;
                  setPickerDirection(spaceBelow < 280 ? 'up' : 'down');
                }
                setShowPicker((v) => !v);
              }}
                className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100" title="리액션">
                <SmilePlus className="size-4" />
              </button>
            )}
            {!isThreadReply && onOpenThread && (
              <button onClick={() => onOpenThread(message.messageId)}
                className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100" title="스레드에 답글">
                <MessageSquare className="size-4" />
              </button>
            )}
            {canPin && (
              isPinned
                ? <button onClick={handleUnpin} className="p-1 text-yellow-500 hover:text-yellow-600 rounded hover:bg-yellow-50" title="고정 해제"><Pin className="size-4" /></button>
                : <button onClick={handlePin} className="p-1 text-gray-500 hover:text-yellow-500 rounded hover:bg-yellow-50" title="메시지 고정"><Pin className="size-4" /></button>
            )}
            {canEdit && (
              <>
                <button onClick={() => { setIsEditing(true); setEditContent(message.content); }}
                  className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100" title="수정">
                  <Pencil className="size-4" />
                </button>
                <button onClick={() => setConfirmDelete(true)}
                  className="p-1 text-gray-500 hover:text-red-500 rounded hover:bg-red-50" title="삭제">
                  <Trash2 className="size-4" />
                </button>
              </>
            )}
            {canOpenTaskModal && (
              <button
                onClick={() => setTaskModalOpen(true)}
                aria-label="작업/할 일로 만들기"
                title="작업/할 일로 만들기"
                className="p-1 text-gray-500 hover:text-primary-600 rounded hover:bg-primary-50"
              >
                <Plus className="size-4" />
              </button>
            )}
            {showPicker && (
              <div className={`absolute right-0 z-50 ${pickerDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                <EmojiPicker onSelect={(emoji) => { handleReactionToggle(emoji); setShowPicker(false); }}
                  onClose={() => setShowPicker(false)} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-2">메시지 삭제</h3>
            <p className="text-sm text-gray-600 mb-4">이 메시지를 삭제하시겠습니까?<br />
              <span className="text-sm text-gray-500">삭제된 메시지는 모든 멤버에게 '삭제된 메시지'로 표시됩니다.</span></p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">취소</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600">삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && imageAttachments.length > 0 && (
        <ImageLightbox
          images={imageAttachments.map((a) => ({ url: a.url, fileName: a.fileName, fileSize: a.fileSize }))}
          initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}

      {/* CHG-123 REQ-V123-021/030~035: 메시지 → 작업 변환 멀티스텝 흐름 */}
      {canOpenTaskModal && (
        <MessageToTaskFlow
          open={taskModalOpen}
          onOpenChange={setTaskModalOpen}
          message={message}
          contextProjectId={channelType === 'PROJECT' ? contextProjectId : undefined}
          contextProjectKey={channelType === 'PROJECT' ? contextProjectKey : undefined}
        />
      )}
    </>
  );
}
